import { useEffect, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ToastProvider } from '@acs/ui';
import {
  AuthActionPage,
  AuthProvider,
  ForgotPasswordPage,
  RequireAuth,
  SignInPage,
  VerifyEmailPage,
} from '../features/auth/index.js';
import { I18nProvider, useI18n } from '../i18n/index.js';
import { DashboardPage } from '../features/appointments/index.js';
import { BusinessesListPage, CreateBusinessPage } from '../features/businesses/index.js';
import { CallsPage } from '../features/calls/index.js';
import { ClientDetailPage, ClientsListPage } from '../features/clients/index.js';
import { SubscribeRedirect } from '../features/billing/index.js';
import { LandingPage } from '../features/landing/index.js';
import { OnboardingWizardPage } from '../features/onboarding/index.js';
import { SettingsPage } from '../features/settings/index.js';
import { StaffEditorPage, StaffListPage } from '../features/staff/index.js';
import { TestCallPage } from '../features/testcall/index.js';
import { AccountLayout } from './AccountLayout.js';
import { ActiveBusinessLayout } from './ActiveBusinessLayout.js';
import { Layout } from './Layout.js';
import { SetupLayout } from './SetupLayout.js';
import { BusinessesProvider } from './BusinessesContext.js';
import { HomeRoute } from './HomeRoute.js';
import { LegacyRedirect, NumberRedirect, UpgradeRedirect } from './LegacyRedirect.js';
import { queryClient } from './queryClient.js';
import { settleBootLoader } from '../shared/ui/bootLoader.js';
// The design system's stylesheet, imported explicitly at the entry rather than
// leaning on the @acs/ui barrel's side-effect import: Rollup tree-shook that
// away in production builds (dev never does, so the app looked fine locally and
// shipped unstyled). Must come before the app styles, which alias its tokens.
import '@acs/ui/styles.css';
import './styles.css';

/**
 * `ToastProvider` with its one piece of chrome translated.
 *
 * The toast ✕ is the only string `@acs/ui` renders that no call site can reach
 * — a toast is raised from a `toast()` call, not from JSX — so the label is
 * supplied once here. It must sit INSIDE `I18nProvider` to read the locale,
 * which is why it is a component rather than a prop on the element in `App`.
 */
function TranslatedToasts({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  return <ToastProvider dismissLabel={t('ui.dismiss')}>{children}</ToastProvider>;
}

export function App() {
  // Routes that never show a page loader (sign-in, landing) still need the
  // boot loader gone; ones that do keep holding it past this check.
  useEffect(() => settleBootLoader(), []);
  return (
    <QueryClientProvider client={queryClient}>
      {/* Outermost of the app providers: toasts, the sign-in screen and every
          error surface below it are translated, so nothing that can render
          text may sit above it. */}
      <I18nProvider>
        <TranslatedToasts>
          <AuthProvider>
            <BrowserRouter>
              <Routes>
                {/* Public. `/` sends a visitor to sign-in and an owner to their
                    own screen (HomeRoute). The marketing page has its own URL
                    but, while the product is in internal testing, NOTHING links
                    to it — `/landing` is typed or it is not reached. It is
                    still routed rather than removed so the copy can be reviewed
                    in the real app; it goes live by adding a link, not by
                    adding a route. Both sit outside RequireAuth. */}
                <Route path="/" element={<HomeRoute />} />
                <Route path="/landing" element={<LandingPage />} />
                <Route path="/signin" element={<SignInPage />} />
                {/* Account emails (doc 88). The first two are public — the
                    person is signed out or has forgotten how to sign in. The
                    verify screen guards itself with `authGate`: RequireAuth
                    would send an unverified account straight back to it. */}
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/auth/action" element={<AuthActionPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route
                  element={
                    <RequireAuth>
                      <BusinessesProvider />
                    </RequireAuth>
                  }
                >
                  {/* Account-level (no active business yet) */}
                  <Route element={<AccountLayout />}>
                    <Route path="/businesses" element={<BusinessesListPage />} />
                  </Route>
                  {/* Creating a business IS step 1 of setup, so it wears the
                      wizard's chrome rather than the account shell. */}
                  <Route element={<SetupLayout />}>
                    <Route path="/businesses/new" element={<CreateBusinessPage />} />
                  </Route>

                  {/* Business-scoped. The layout resolves the business and picks
                      between the setup wizard's chrome and the full app shell. */}
                  <Route path="/b/:businessId" element={<ActiveBusinessLayout />}>
                    <Route element={<SetupLayout />}>
                      <Route path="setup" element={<OnboardingWizardPage />} />
                    </Route>
                    {/* Retired pages: subscribing lives in the wizard's
                        Activate step and Settings › Billing. Kept for old links
                        and Checkout sessions opened before the change. */}
                    <Route path="subscribe" element={<SubscribeRedirect />} />
                    <Route path="subscribe/done" element={<SubscribeRedirect />} />
                    <Route element={<Layout />}>
                      <Route index element={<DashboardPage />} />
                      <Route path="calls" element={<CallsPage />} />
                      <Route path="clients" element={<ClientsListPage />} />
                      <Route path="clients/:e164" element={<ClientDetailPage />} />
                      <Route path="test" element={<TestCallPage />} />
                      <Route path="staff" element={<StaffListPage />} />
                      <Route path="staff/:staffId" element={<StaffEditorPage />} />
                      <Route path="settings" element={<SettingsPage />} />
                      {/* The number moved into Settings › Business; keep the old
                          path working for bookmarks and in-flight links. */}
                      <Route path="number" element={<NumberRedirect />} />
                    </Route>
                  </Route>

                  {/* The short link in the minute-allowance warning SMS
                      (doc 26 D8). It exists purely to be short: a warning has
                      to carry a URL inside a 160-character segment, and
                      `/b/{id}/settings?section=billing` is 25 characters longer. */}
                  <Route path="/u/:businessId" element={<UpgradeRedirect />} />

                  {/* Legacy URLs → same section under the resolved active
                      business. `/` is no longer among them: it is the public
                      landing page now, and a signed-in owner is routed from
                      there by account shape rather than by last-active. */}
                  <Route path="/calls" element={<LegacyRedirect section="calls" />} />
                  <Route path="/test" element={<LegacyRedirect section="test" />} />
                  <Route path="/settings" element={<LegacyRedirect section="settings" />} />
                  <Route path="/onboarding" element={<LegacyRedirect section="number" />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </TranslatedToasts>
      </I18nProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
